import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { PatientHeader } from '../../features/admissions/PatientHeader';
import { PageHeader } from '~/shared/PageHeader';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { ChartPageNavigation } from './ChartPageNavigation';
import { PageInset } from '~/shared/PageInset';
import { CertificationPeriodSelector } from '../../features/admissions/cert-periods/StayCertPeriodSelector';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getAdmissionHistoryOptions } from '@generated';
import { isBetweenOrEqualToByDay } from '~/util/date';
import { useToday } from '~/util/useToday';
import { ChartContextProvider } from '../../features/admissions/charts/ChartContext';

export function ChartPage() {
	const { pathname } = useLocation();
	const today = useToday();
	const params = useParams<'patientId' | 'stayId' | 'certificationPeriodId' | 'taskId'>();
	if (!params.stayId || !params.patientId || !params.certificationPeriodId) {
		throw new Error('stayId, patientId, and certificationPeriodId are required in the URL parameters');
	}
	const { data: admissions } = useSuspenseQuery(getAdmissionHistoryOptions(params.patientId));

	const selectedCertificationPeriod = admissions
		.flatMap(x => x.certificationPeriods)
		.find(
			x =>
				(params.certificationPeriodId === 'current' && isBetweenOrEqualToByDay(x.startDate, x.endDate, today)) ||
				x.id === params.certificationPeriodId
		);

	if (!selectedCertificationPeriod) {
		throw new Error('Selected certification period not found');
	}

	return (
		<SuspenseWrapper name="ChartPage" key={params.patientId}>
			<ChartContextProvider
				patientId={params.patientId}
				stayId={params.stayId}
				certificationPeriodId={selectedCertificationPeriod.id}
				workflowStageTaskId={params.taskId}
			>
				<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
					<PageHeader
						title="Chart"
						description="View and manage all patient information, documentation, and tasks in one place"
						titleRightSlot={
							<div className="flex items-center gap-2">
								<CertificationPeriodSelector />
								<PatientHeader stayId={params.stayId} />
							</div>
						}
					/>
					<main className="flex min-h-0 flex-1 flex-col">
						<PageInset>
							<PageInset.Nav>
								<ChartPageNavigation />
							</PageInset.Nav>
							<PageInset.Body key={pathname}>
								<SuspenseWrapper name="ChartPage">
									<div className="mx-auto max-w-7xl p-8">
										<Outlet />
									</div>
								</SuspenseWrapper>
							</PageInset.Body>
						</PageInset>
					</main>
				</div>
			</ChartContextProvider>
		</SuspenseWrapper>
	);
}
